import React from "react";
import {
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export type ResponseVariant = "success" | "info" | "warning" | "error";

interface ResponseMessageProps {
  variant: ResponseVariant;
  message: React.ReactNode; // accepts string or rich content
  className?: string;
  role?: string; // e.g. 'alert'
  // optional custom icon node; if provided it will be placed to the left of the message
  icon?: React.ReactNode;
  // auto dismiss after the given number of milliseconds (default 6000). If 0 or undefined, no auto-dismiss.
  duration?: number;
  // optional callback invoked when the message auto-closes (or manual close if implemented by parent)
  onClose?: () => void;
}

// Centralized color map and class composition to avoid repetition.
const colorMap: Record<ResponseVariant, string> = {
  success: "green-600",
  info: "blue-600",
  warning: "yellow-600",
  error: "red-600",
};

const baseContainer =
  "m-2 p-2 rounded-lg flex items-center w-auto max-w-[90%] mx-auto transition duration-300 ease-in-out";

// Default icon components per variant to keep rendering concise.
const defaultIcons: Record<ResponseVariant, React.ComponentType<any>> = {
  success: CheckCircleIcon,
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  error: ExclamationTriangleIcon,
};

// Inline SVG used in the templates (information circle). Kept consistent across variants.
const InfoIcon = ({ className }: { className?: string }) => (
  <svg
    stroke="currentColor"
    viewBox="0 0 24 24"
    fill="none"
    className={className ?? "h-5 w-5 flex-shrink-0 mr-3"}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13 16h-1v-4h1m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      strokeWidth="2"
      strokeLinejoin="round"
      strokeLinecap="round"
    ></path>
  </svg>
);

export default function ResponseMessage({
  variant,
  message,
  className,
  role = "alert",
  icon,
  duration = 6000,
  onClose,
}: ResponseMessageProps) {
  // Do not render when there's no meaningful message to show.
  if (
    message === undefined ||
    message === null ||
    (typeof message === "string" && message.trim() === "")
  ) {
    return null;
  }
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    // When a new message arrives, make it visible and start the timer (if duration > 0).
    setVisible(true);
    if (!duration || duration <= 0) return;
    const t = setTimeout(() => {
      setVisible(false);
      try {
        onClose && onClose();
      } catch (e) {
        // swallow errors from onClose to avoid breaking UI
      }
    }, duration);
    return () => clearTimeout(t);
  }, [message, duration, onClose]);

  if (!visible) return null;
  const color = colorMap[variant];
  const container = `${baseContainer} text-${color} border-${color}`;
  const iconClass = `h-9 w-9 flex-shrink-0 mr-3 text-${color}`;
  const IconComp = defaultIcons[variant] ?? InfoIcon;
  return (
    <div
      role={role}
      className={[container, className].filter(Boolean).join(" ")}
      // Use inset shadows on both left and right using currentColor so the
      // shadow matches the variant text color. This replaces the previous
      // left/right borders.
      style={{
        boxShadow:
          "inset 6px 0 12px -8px currentColor, inset -6px 0 12px -8px currentColor",
      }}
    >
      {/* Render custom icon if provided, otherwise use the default InfoIcon. */}
      {icon ? (
        <span className={iconClass}>{icon}</span>
      ) : (
        <IconComp className={iconClass} />
      )}
      <p className="text-xs font-semibold whitespace-normal">{message}</p>
    </div>
  );
}
